import { computed, effect, Injectable, Signal, signal, WritableSignal } from '@angular/core';

import { ThemeUtilsService } from '@core/theme-logic';
import { RECRUITEE_THEME_LS_KEY, SupportedThemesT } from '@core/theme-util';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  public readonly isDarkMode: Signal<boolean>;
  public readonly currentTheme: Signal<SupportedThemesT>;

  private theme!: WritableSignal<SupportedThemesT>;
  private hasUserPreference!: boolean;

  constructor() {
    // Initialize signals
    this.theme = signal<SupportedThemesT>(this.getInitialTheme());
    this.hasUserPreference = this.checkHasUserPreference();
    this.isDarkMode = computed(() => this.theme() === 'dark');
    this.currentTheme = this.theme.asReadonly();

    // Apply theme to DOM whenever it changes
    effect(() => {
      const theme = this.theme();
      ThemeUtilsService.setThemeHTMLData(theme);
    });

    // Apply initial theme immediately
    ThemeUtilsService.setThemeHTMLData(this.theme());

    // Listen for system theme changes (only applies if user hasn't set preference)
    this.listenToSystemThemeChanges();
  }

  public toggleTheme(): void {
    this.theme.update(current => (current === 'light' ? 'dark' : 'light'));
    this.persistTheme(this.theme());
    this.hasUserPreference = true;
  }

  public setTheme(theme: SupportedThemesT): void {
    this.theme.set(theme);
    this.persistTheme(theme);
    this.hasUserPreference = true;
  }

  private getInitialTheme(): SupportedThemesT {
    // First check for user preference in localStorage
    const stored = localStorage.getItem(RECRUITEE_THEME_LS_KEY);
    if (stored === 'dark' || stored === 'light') {
      return stored;
    }

    // Fall back to system preference
    return this.getSystemTheme();
  }

  private checkHasUserPreference(): boolean {
    const stored = localStorage.getItem(RECRUITEE_THEME_LS_KEY);
    return stored === 'dark' || stored === 'light';
  }

  private getSystemTheme(): SupportedThemesT {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  }

  private persistTheme(theme: SupportedThemesT): void {
    localStorage.setItem(RECRUITEE_THEME_LS_KEY, theme);
  }

  private listenToSystemThemeChanges(): void {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', event => {
      // Only auto-switch if user hasn't set a preference
      if (!this.hasUserPreference) {
        this.theme.set(event.matches ? 'dark' : 'light');
      }
    });
  }
}
